import { z } from "zod";
import { materialScrapWriteSchema } from "../../../shared/production/material-scrap";
import { validQuantityPrecision } from "../../../shared/production/quantity";
import { idbResult, localTransaction, notifyLocalDatabaseChanged } from "./operator-local-database";
import type { LocalDraftRef } from "./quantity-outbox-store";
import { quantityDraftSchema } from "./quantity-write-queue";
import type { CriticalWriteDeliveryState } from "./critical-write-failure";

const deliveryStateSchema: z.ZodType<CriticalWriteDeliveryState> = z.enum([
  "PREPARED", "SENDING", "OUTCOME_UNKNOWN", "REMOTE_CONFIRMED",
]);

const historicalProductPayloadSchema = quantityDraftSchema.omit({ quantity: true }).extend({
  quantity: z.number().refine(validQuantityPrecision),
  occurredAt: z.string().datetime(), idempotencyKey: z.string().uuid(), expectedRunVersion: z.number().int().positive(), expectedSessionId: z.string().min(1),
}).strict();
const materialPayloadSchema = materialScrapWriteSchema.omit({ approvalToken: true }).extend({ expectedSessionId: z.string().min(1) }).strict();
export const criticalWriteSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("HISTORICAL_PRODUCT_SCRAP"), idempotencyKey: z.string().uuid(), terminalCode: z.string().min(1), operatorId: z.string().min(1),
    sessionId: z.string().min(1), runId: z.string().min(1), createdAt: z.string().datetime(),
    deliveryState: deliveryStateSchema.default("PREPARED"), payload: historicalProductPayloadSchema }).strict(),
  z.object({ kind: z.literal("MATERIAL_SCRAP"), idempotencyKey: z.string().uuid(), terminalCode: z.string().min(1), operatorId: z.string().min(1),
    sessionId: z.string().min(1), runId: z.string().min(1), createdAt: z.string().datetime(),
    deliveryState: deliveryStateSchema.default("PREPARED"), payload: materialPayloadSchema }).strict(),
]).superRefine((item, context) => {
  if (item.idempotencyKey !== item.payload.idempotencyKey) {
    context.addIssue({ code: "custom", path: ["payload", "idempotencyKey"],
      message: "Kritik kayıt ile yükün işlem kimliği eşleşmiyor." });
  }
});
export type CriticalWrite = z.infer<typeof criticalWriteSchema>;
export type CriticalWriteKind = CriticalWrite["kind"];
const draftRefSchema = z.object({ key: z.string(), intentId: z.string().uuid(), revision: z.number().int().nonnegative() });

export async function loadCriticalWrite(preview: boolean, identity: { terminalCode: string; operatorId: string; sessionId: string; runId: string }, kind: CriticalWriteKind) {
  const raw: unknown[] = await localTransaction(preview,["criticalWrites"],"readonly",(tx)=>idbResult(tx.objectStore("criticalWrites").index("owner").getAll([
    identity.terminalCode,identity.operatorId,identity.sessionId,identity.runId,kind,
  ])));
  const entries=z.array(criticalWriteSchema).parse(raw);
  if (entries.length > 1) throw new Error("Bu iş için birden fazla kritik kayıt bekliyor. Yeni kayıt açmadan önce sonuçları denetleyin.");
  return entries[0] ?? null;
}

export async function saveCriticalWrite(preview: boolean, item: CriticalWrite, draft?: LocalDraftRef) {
  const validated=criticalWriteSchema.parse(item);
  await localTransaction(preview,draft?["criticalWrites","drafts"]:["criticalWrites"],"readwrite",async(tx)=>{
    const store=tx.objectStore("criticalWrites");
    const existing:unknown=await idbResult(store.index("owner").getAll([item.terminalCode,item.operatorId,item.sessionId,item.runId,item.kind]));
    const entries=z.array(criticalWriteSchema).parse(existing);
    if (entries.some((entry)=>entry.idempotencyKey!==item.idempotencyKey)) throw new Error("Önce bekleyen kritik kaydın sonucunu doğrulayın.");
    if (draft) {
      const stored: unknown = await idbResult(tx.objectStore("drafts").get(draft.key));
      const current = draftRefSchema.safeParse(stored);
      if (!current.success || current.data.intentId !== draft.intentId || current.data.revision !== draft.revision) {
        throw new Error("Taslak başka bir pencerede değişti veya zaten kritik kayda dönüştürüldü. Güncel taslağı kontrol edin.");
      }
    }
    await idbResult(store.put(validated));
    if (draft) await idbResult(tx.objectStore("drafts").delete(draft.key));
  });
  notifyLocalDatabaseChanged();
}

export async function removeCriticalWrite(preview:boolean,idempotencyKey:string) {
  await localTransaction(preview,["criticalWrites"],"readwrite",async(tx)=>{await idbResult(tx.objectStore("criticalWrites").delete(idempotencyKey));});
  notifyLocalDatabaseChanged();
}
